import React from 'react';
import {
  Badge,
  Button,
  Card,
  formatCurrency,
  useIsMobile,
} from '@sandtech-hub-monorepo/ui-kit';
import { GetSemesterPaymentPlansQuery } from '@sandtech-hub-monorepo/ehub-client';
import { formatDate } from '@sandtech-hub-monorepo/ui-kit-site';
import { IncompleteTransactionsSection } from '@sandtech-hub-monorepo/ui-kit-site';
import { ReceiptsSection } from '@sandtech-hub-monorepo/ui-kit-site';
import { Link } from 'react-router-dom';

const lastWeeks = [
  'Week 9',
  'Week 10',
  'Week 11',
  'Week 12',
  'Week 13',
  'Week 14',
  'Week 15',
  'Week 16',
];
const lastWeeksRefunds = ['46%', '40%', '0%', '0%', '0%', '0%', '0%', '0%'];

interface Props {
  paymentPlan: GetSemesterPaymentPlansQuery['viewer']['paymentPlans'][number];
}

export const CancelledPaymentsTab = ({ paymentPlan }: Props) => {
  const isMobile = useIsMobile();
  const cancellationAmount = paymentPlan.cancellationAmount ?? 0;
  const isOwed = cancellationAmount > 0;
  const viewUrl = paymentPlan?.viewUrl || '';

  return (
    <div className="flex flex-col gap-8">
      <div className="flex gap-4 items-center flex-wrap">
        <p className="font-semibold text-lg">Payment status</p>
        <Badge variant="destructive">Canceled</Badge>
        <p className="text-sm text-muted-foreground">
          Payment plan cancelled on{' '}
          {formatDate(paymentPlan.cancelDate)}
        </p>
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-lg">Summary</p>
          <div className="flex gap-6 flex-wrap">
            <Card className="flex-1 border-muted p-6 flex flex-col gap-2 min-w-48">
              <p className="font-semibold text-lg">Amount Paid</p>
              <p className="font-semibold text-4xl">
                {formatCurrency(paymentPlan.totalPaidAmount ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">
                Total amount paid this semester.
              </p>
            </Card>

            <Card
              className={`flex-1 p-6 flex flex-col gap-2 min-w-48 ${
                isOwed
                  ? 'border-destructive bg-destructive-soft'
                  : 'border-muted'
              }`}
            >
              <p className="font-semibold text-lg">
                {isOwed ? 'Amount Owed' : 'Amount to be Refunded'}
              </p>
              <div className="flex justify-between items-center">
                <p
                  className={`font-semibold text-4xl ${
                    isOwed
                      ? 'text-destructive'
                      : cancellationAmount
                      ? 'text-success'
                      : ''
                  }`}
                >
                  {formatCurrency(Math.abs(cancellationAmount))}
                </p>
                {isOwed && viewUrl && (
                  <Link to={viewUrl} target="_blank" rel="noopener noreferrer">
                    <Button>Pay Remaining Balance</Button>
                  </Link>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {isOwed ? (
                  'You have an outstanding balance to pay.'
                ) : (
                  <>
                    This amount represents the total you'll be refunded.
                    <br />
                    Please contact support if you have any issues
                  </>
                )}
              </p>
            </Card>
          </div>
        </div>
        {cancellationAmount < 0 && (
          <Card className="border-muted p-6 pb-12 flex flex-col gap-6 items-center">
            <p className="font-semibold text-lg">
              Refund calculation example (1 semester)
            </p>
            <div className="flex gap-12 text-center flex-wrap">
              <div className="flex-1 flex">
                <div className="flex flex-col gap-2">
                  <div className="bg-background-tertiary rounded-md font-semibold px-4">
                    Week of Withdrawal
                  </div>
                  {[
                    'Week 1',
                    'Week 2',
                    'Week 3',
                    'Week 4',
                    'Week 5',
                    'Week 6',
                    'Week 7',
                    'Week 8',
                    ...(isMobile ? lastWeeks : []),
                  ].map((week) => (
                    <div key={week}>{week}</div>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="bg-background-tertiary rounded-md font-semibold px-4">
                    Refund
                  </div>
                  {[
                    '100%',
                    '100%',
                    '86%',
                    '80%',
                    '73%',
                    '66%',
                    '60%',
                    '53%',
                    ...(isMobile ? lastWeeksRefunds : []),
                  ].map((percent, idx) => (
                    <div key={idx} className="font-semibold">
                      {percent}
                    </div>
                  ))}
                </div>
              </div>
              {/* Divider */}
              <div className="w-px self-stretch bg-muted hidden sm:block" />
              <div className="flex-1 hidden sm:flex">
                <div className="flex flex-col gap-2">
                  <div className="bg-background-tertiary rounded-md font-semibold px-4">
                    Week of Withdrawal
                  </div>
                  {lastWeeks.map((week) => (
                    <div key={week}>{week}</div>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="bg-background-tertiary rounded-md font-semibold px-4">
                    Refund
                  </div>
                  {lastWeeksRefunds.map((percent, idx) => (
                    <div key={idx} className="font-semibold">
                      {percent}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-base leading-6 text-center max-w-2xl">
                For example, if a student who has paid $225 withdraws from the
                FIT during their fourth week of the semester, the student would
                receive a refund of $180.
              </p>
            </div>
            <div className="bg-background-tertiary border border-muted rounded-2xl flex items-center justify-center max-w-[769px]">
              <div className="flex items-center justify-center gap-4 text-base leading-6 text-center px-6">
                <p className="font-semibold">$225</p>
                <p>x</p>
                <div className="flex flex-col items-center max-w-[220px]">
                  <p className="font-semibold">80%</p>
                  <p className="text-sm">
                    (Student withdraws during Week 4 of the semester)
                  </p>
                </div>
                <p>=</p>
                <p className="font-semibold">$180</p>
              </div>
            </div>
          </Card>
        )}
      </div>
      <IncompleteTransactionsSection
        incompleteTransactions={paymentPlan?.incompleteTransactions || []}
      />
      <div>
        <p className="font-semibold text-lg">Payment History</p>
        <ReceiptsSection payments={paymentPlan?.payments || []} />
      </div>
    </div>
  );
};
